import { ThemeProvider } from '@repo/ui';
import { lazy, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AuthProvider from './context/AuthProvider.js';
import Cookie from './layouts/Cookie.js';
import SchoolAuthLayout from './layouts/SchoolAuthLayout.js';
import SchoolLayout from './layouts/SchoolLayout.js';
import { TrpcProvider } from './utils/TrpcProvider.js';

type DynamicImport<T> = () => Promise<{ default: React.ComponentType<T> }>;
// Utility function to handle dynamic imports
async function importOrReload<T>(importFunction: DynamicImport<T>) {
  try {
    return await importFunction();
  } catch (error) {
    if (
      error instanceof Error &&
      error.message.includes("dynamically imported module")
    ) {
      window.location.reload();
      return { default: () => null };
    } else {
      // Re-throw the error if it's not the specific one we're looking for
      throw error;
    }
  }
}

function IntegrationsRedirect() {
  const location = useLocation();

  return (
    <Navigate
      to={{ pathname: '/agent/settings', search: location.search }}
      replace
    />
  );
}

// Layouts
import AgentAppLayout from './layouts/AgentAppLayout.js';
import AgentAuthLayout from './layouts/AgentAuthLayout.js';
import AppLayout from './layouts/AppLayout.js';
import AuthLayout from './layouts/AuthLayout.js';
import MagicLinkLayout from './layouts/MagicLinkLayout.js';
import VoiceDemoLayout from './layouts/VoiceDemoLayout.js';

// Auth pages
const LoginPage = lazy(() => importOrReload(() => import('./pages/auth/LoginPage.js')));
const RegisterPage = lazy(() => importOrReload(() => import('./pages/auth/signup/RegisterPage.js')));
const SignupDetailsPage = lazy(() => importOrReload(() => import('./pages/auth/signup/DetailsPage.js')));

// School Auth pages
const SchoolLoginPage = lazy(() => importOrReload(() => import('./pages/school/auth/LoginPage.js')));
const SchoolSignupPage = lazy(() => importOrReload(() => import('./pages/school/auth/SignupPage.js')));
const SchoolDetailsPage = lazy(() => importOrReload(() => import('./pages/school/auth/StaffDetails.js')));
const ConvertToSchoolUser = lazy(() => importOrReload(() => import('./pages/school/auth/ConvertToStaff.js')));

// School App pages
const SchoolCounselorDashboard = lazy(() => importOrReload(() => import('./pages/school/dashboard/CounselorDashboard.js')));
const SchoolOrganizationSetup = lazy(() => importOrReload(() => import('./pages/school/organization/OrganizationSetup.js')));
const SchoolInvitationDashboard = lazy(() => importOrReload(() => import('./pages/school/invitations/InvitationDashboard.js')));
const SchoolStudentDetail = lazy(() => importOrReload(() => import('./pages/school/dashboard/StudentDetailView.js')));
const SchoolStudentEssayView = lazy(() => importOrReload(() => import('./pages/school/students/StudentEssayView.js')));
const SchoolBulkInvitePage = lazy(() => importOrReload(() => import('./pages/school/invitations/BulkInvitePage.js')));
const SchoolCalendarPage = lazy(() => importOrReload(() => import('./pages/school/calendar/CalendarPage.js')));
const SchoolDocumentRequestsDashboard = lazy(() => importOrReload(() => import('./pages/school/document-requests/index.js')));

// App pages
const CalendarPage = lazy(() => importOrReload(() => import('./pages/app/calendar/CalendarPage.js')));
const ProfileEditPage = lazy(() => importOrReload(() => import('./pages/app/profile/ProfileEditPage.js')));
const ProfileHomePage = lazy(() => importOrReload(() => import('./pages/app/profile/ProfileHome.js')));
const EssaysPage = lazy(() => importOrReload(() => import('./pages/app/courses/essays/Essays.js')));
const WatchlistPage = lazy(() => importOrReload(() => import('./pages/app/watchlist/Watchlist.js')));
const CourseDiscoverPage = lazy(() => importOrReload(() => import('./pages/app/courses/discover/index.js')));
const CourseDetailPage = lazy(() => importOrReload(() => import('./pages/app/courses/CourseDetail.js')));
const CourseApplicationPage = lazy(() => importOrReload(() => import('./pages/app/courses/CourseApplication.js')));
const RecommendCoursesPage = lazy(() => importOrReload(() => import('./pages/app/courses/Recommend.js')));

// Psychometric assessment pages
const PsychometricTest = lazy(() => importOrReload(() => import('./pages/app/profile/psychometric/PsychometricTest.js')));
const PsychometricResults = lazy(() => importOrReload(() => import('./pages/app/profile/psychometric/PsychometricResults.js')));

// Scholarship pages
const ScholarshipDiscoverPage = lazy(() => importOrReload(() => import('./pages/app/scholarships/Discover.js')));
const ScholarshipDetailPage = lazy(() => importOrReload(() => import('./pages/app/scholarships/ScholarshipDetail.js')));

// Short Courses pages
const ShortCoursesDiscoverPage = lazy(() => importOrReload(() => import('./pages/app/short-courses/DiscoverPage.js')));
const ShortCourseDetailsPage = lazy(() => importOrReload(() => import('./pages/app/short-courses/ShortCourseDetailsPage.js')));

// Referrals page
const ReferralsPage = lazy(() => importOrReload(() => import('./pages/app/referrals/ReferralsPage.js')));

// Document Request page
const DocumentRequestPage = lazy(() => importOrReload(() => import('./pages/app/documents/request/index.js')));

// Demo pages
const DemoHomePage = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage.js')));
const DemoHomePage_Hotel = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_Hotel.js')));
const DemoHomePage_MiCampus = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_MiCampus.js')));
const DemoHomePage_LBFTravel = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_LBFTravel.js')));
const DemoHomePage_Europcar = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_Europcar.js')));
const DemoHomePage_CasaAndina = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_CasaAndina.js')));
const DemoHomePage_Corinthia = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_Corinthia.js')));
const DemoHomePage_Deyaar = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_Deyaar.js')));
const DemoHomePage_SunLife = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_SunLife.js')));
const DemoHomePage_Yugo = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_Yugo.js')));
const DemoHomePage_Vamos = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_Vamos.js')));
const DemoHomePage_MGMMacau = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_MGMMacau.js')));
const DemoHomePage_PHH = lazy(() => importOrReload(() => import('./pages/demo/DemoHomePage_PHH.js')));

// Chat page
const ChatPage = lazy(() => importOrReload(() => import('./pages/app/ChatPage.js')));

// Voice Demo pages
const VoiceDemoDashboard = lazy(() => importOrReload(() => import('./pages/voice_demo/Dashboard.js')));
const VoiceDemoCallHistory = lazy(() => importOrReload(() => import('./pages/voice_demo/CallHistoryPage.js')));
const VoiceDemoScriptEditor = lazy(() => importOrReload(() => import('./pages/voice_demo/ScriptEditorPage.js')));
const VoiceDemoContactManagement = lazy(() => importOrReload(() => import('./pages/voice_demo/ContactManagementPage.js')));
const VoiceDemoFollowUps = lazy(() => importOrReload(() => import('./pages/voice_demo/FollowUpsPage.js')));
const VoiceDemoABTesting = lazy(() => importOrReload(() => import('./pages/voice_demo/ABTestingPage.js')));
const VoiceDemoMassInsights = lazy(() => importOrReload(() => import('./pages/voice_demo/MassInsightsPage.js')));
const VoiceDemoIntegration = lazy(() => importOrReload(() => import('./pages/voice_demo/IntegrationPage.js')));
const VoiceDemoVerificationDashboard = lazy(() => importOrReload(() => import('./pages/voice_demo/VerificationDashboard.js')));
const VoiceDemoDocumentVerification = lazy(() => importOrReload(() => import('./pages/voice_demo/DocumentVerificationPage.js')));
const VoiceDemoIdentityVerification = lazy(() => importOrReload(() => import('./pages/voice_demo/IdentityVerificationPage.js')));
const VoiceDemoVerificationAnalytics = lazy(() => importOrReload(() => import('./pages/voice_demo/VerificationAnalyticsPage.js')));

// Agent Auth pages
const AgentLoginPage = lazy(() => importOrReload(() => import('./pages/agent/auth/AgentLoginPage.js')));

// Agent pages
const AgentNotificationsPage = lazy(() => importOrReload(() => import('./pages/agent/NotificationsPage.js')));
const AgentHomePage = lazy(() => importOrReload(() => import('./pages/agent/HomePage.js')));
const AgentContactListPage = lazy(() => importOrReload(() => import('./pages/agent/ContactListPage.js')));
const AgentContactDetailPage = lazy(() => importOrReload(() => import('./pages/agent/ContactDetailPage.js')));
const AgentKnowledgeBankPage = lazy(() => importOrReload(() => import('./pages/agent/KnowledgeBankPage.js')));
const AgentSettingsPage = lazy(() => importOrReload(() => import('./pages/agent/SettingsPage.js')));
const AgentAccountSettingsPage = lazy(() => importOrReload(() => import('./pages/agent/AccountSettingsPage.js')));
const AgentFoundryPage = lazy(() => importOrReload(() => import('./pages/agent/FoundryPage.js')));

// Agent Insights pages
const AgentInsightsOverviewPage = lazy(() => importOrReload(() => import('./pages/agent/insights/InsightsOverviewPage.js')));
const AgentInsightsPerformancePage = lazy(() => importOrReload(() => import('./pages/agent/insights/PerformancePage.js')));
const AgentInsightsContentPage = lazy(() => importOrReload(() => import('./pages/agent/insights/ContentInsightsPage.js')));
const AgentInsightsWorkflowsPage = lazy(() => importOrReload(() => import('./pages/agent/insights/WorkflowComparisonPage.js')));
const AgentInsightsTimePatternsPage = lazy(() => importOrReload(() => import('./pages/agent/insights/TimePatternsPage.js')));
const AgentInsightsCategoriesPage = lazy(() => importOrReload(() => import('./pages/agent/insights/CategoryManagementPage.js')));
const AgentInsightsDropoffsPage = lazy(() => importOrReload(() => import('./pages/agent/insights/DropoffAnalyticsPage.js')));
const AgentInsightsRevivalPage = lazy(() => importOrReload(() => import('./pages/agent/insights/RevivalPerformancePage.js')));

// Agent Reminders page
const AgentRemindersPage = lazy(() => importOrReload(() => import('./pages/agent/RemindersPage.js')));

// Agent Admin pages
const AgentAdminClientsPage = lazy(() => importOrReload(() => import('./pages/agent/admin/ClientsPage.js')));
const AgentAdminCreateClientPage = lazy(() => importOrReload(() => import('./pages/agent/admin/CreateClientPage.js')));


// Magic Link pages
const ReferenceLetterUploadPage = lazy(() => importOrReload(() => import('./pages/magic/uploads/reference_letter/index.js')));
const DeleteAccountPage = lazy(() => importOrReload(() => import('./pages/magic/account/DeleteAccount.js')));
const BookMeetingPage = lazy(() => importOrReload(() => import('./pages/magic/book-meeting/index.js')));

export default function App() {

  // https://felixcarmona.com/solving-google-login-issues-linkedin-ios/
  // Issue with Google/Firebase OAuth on giving 403 diallowed_useragent on embedded webview
  useEffect(() => {
    const userAgent = window.navigator.userAgent;
    const url = window.location.href;

    // Check if we're in LinkedIn's in-app browser on iOS
    if (
      userAgent.includes('Mobile') &&
      (userAgent.includes('iPhone') || userAgent.includes('iPad')) &&
      userAgent.includes('LinkedInApp')
    ) {
      // Redirect to Safari using the x-safari- URL scheme
      // This is supported starting from iOS 17
      window.location.href = 'x-safari-' + url;
      return;
    }
  }, []);
  return (
    // TODO: When we wanna enable dark mode for web-app, we can remove forceLight
    <ThemeProvider forceLight>
      <TrpcProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              {/* Auth routes */}
              <Route path="/auth" element={<AuthLayout />}>
                <Route path="login" element={<LoginPage />} />
                <Route path="signup" element={<RegisterPage />} />
                {/* <Route path="oauth/callback" element={<OAuthCallbackPage />} /> */}
                <Route path="signup/details" element={<SignupDetailsPage />} />
              </Route>

              {/* Staff auth routes */}
              <Route path="/school/auth" element={<SchoolAuthLayout />}>
                <Route path="login" element={<SchoolLoginPage />} />
                <Route path="signup" element={<SchoolSignupPage />} />
                <Route path="signup/details" element={<SchoolDetailsPage />} />
                <Route path="existingUser" element={<ConvertToSchoolUser />} />
                <Route path="*" index element={<Navigate to="/school/auth/login" replace />} />
              </Route>

              <Route path="/school/app" element={<SchoolLayout />}>
                <Route path="students" element={<SchoolCounselorDashboard />} />
                <Route path="students/:studentId" element={<SchoolStudentDetail />} />
                <Route path="students/:studentId/essays/:courseId" element={<SchoolStudentEssayView />} />
                <Route path="organization" element={<SchoolOrganizationSetup />} />
                <Route path="organization/:tab" element={<SchoolOrganizationSetup />} />
                <Route path="organization/:tab/:action" element={<SchoolOrganizationSetup />} />
                <Route path="organization/:tab/:action/:id" element={<SchoolOrganizationSetup />} />
                <Route path="invitations" element={<SchoolInvitationDashboard />} />
                <Route path="invitations/bulk" element={<SchoolBulkInvitePage />} />
                <Route path="courses/search" element={<CourseDiscoverPage courseCardBaseUrl="/school/app/courses" />} />
                <Route path="courses/:id" element={<CourseDetailPage />} />
                <Route path="scholarships/search" element={<ScholarshipDiscoverPage scholarshipCardBaseUrl="/school/app/scholarships" />} />
                <Route path="scholarships/:scholarshipId" element={<ScholarshipDetailPage />} />
                <Route path="calendar" element={<SchoolCalendarPage />} />
                <Route path="document-requests" element={<SchoolDocumentRequestsDashboard />} />
                <Route path="*" index element={<Navigate to="/school/app/students" replace />} />
              </Route>
              <Route path='/school/*' element={<Navigate to="/school/app/students" replace />} index />

              {/* Shared app routes */}
              <Route path="/app" element={<AppLayout withAuth />}>
                <Route index element={<Navigate to="/app/profile" replace />} />
                <Route path="profile" element={<ProfileHomePage />} />
                <Route path="profile/edit" element={<ProfileEditPage />} />
                <Route path="profile/edit/:section" element={<ProfileEditPage />} />
                <Route path="profile/psychometric" element={<PsychometricTest />} />
                <Route path="profile/psychometric/results" element={<PsychometricResults />} />
                <Route path="calendar" element={<CalendarPage />} />
                <Route path="chat" element={<ChatPage />} />
                <Route path="referrals" element={<ReferralsPage />} />
                <Route path="documents/request" element={<DocumentRequestPage />} />
                {/* New Unified Routes - using extended UK components */}
                <Route path="essays" element={<EssaysPage />} />
                <Route path="watchlist" element={<WatchlistPage />} />
                <Route path="courses/discover" element={<CourseDiscoverPage />} />
                <Route path="courses/discover/:id" element={<CourseDetailPage />} />
                <Route path="courses/recommend" element={<RecommendCoursesPage />} />
                <Route path="courses/apply/:id" element={<CourseApplicationPage />} />
                <Route path="scholarships/discover" element={<ScholarshipDiscoverPage />} />
                <Route path="scholarships/discover/:scholarshipId" element={<ScholarshipDetailPage />} />
                <Route path="short-courses/discover" element={<ShortCoursesDiscoverPage />} />
                <Route path="short-courses/details/:id" element={<ShortCourseDetailsPage />} />
                <Route path="integrations" element={<IntegrationsRedirect />} />

                <Route path="*" element={<Navigate to="/app/profile" replace />} />
              </Route>

              {/* Voice Demo routes - no auth required */}
              <Route path="/voice_demo" element={<VoiceDemoLayout />}>
                <Route index element={<VoiceDemoDashboard />} />
                <Route path="calls" element={<VoiceDemoCallHistory />} />
                <Route path="scripts" element={<VoiceDemoScriptEditor />} />
                <Route path="contacts" element={<VoiceDemoContactManagement />} />
                <Route path="followups" element={<VoiceDemoFollowUps />} />
                <Route path="ab_testing" element={<VoiceDemoABTesting />} />
                <Route path="insights" element={<VoiceDemoMassInsights />} />
                <Route path="integration" element={<VoiceDemoIntegration />} />
                <Route path="verification" element={<VoiceDemoVerificationDashboard />} />
                <Route path="verification/documents" element={<VoiceDemoDocumentVerification />} />
                <Route path="verification/identity" element={<VoiceDemoIdentityVerification />} />
                <Route path="verification/analytics" element={<VoiceDemoVerificationAnalytics />} />
              </Route>

              {/* Agent auth routes */}
              <Route path="/agent/auth" element={<AgentAuthLayout />}>
                <Route path="login" element={<AgentLoginPage />} />
                <Route path="*" index element={<Navigate to="/agent/auth/login" replace />} />
              </Route>

              {/* Agent routes - requires authentication */}
              <Route path="/agent" element={<AgentAppLayout />}>
                <Route index element={<AgentHomePage />} />
                <Route path="notifications" element={<AgentNotificationsPage />} />
                <Route path="contacts" element={<AgentContactListPage />} />
                <Route path="contacts/:id" element={<AgentContactDetailPage />} />
                <Route path="reminders" element={<AgentRemindersPage />} />
                {/* Insights routes */}
                <Route path="insights" element={<AgentInsightsOverviewPage />} />
                <Route path="insights/performance" element={<AgentInsightsPerformancePage />} />
                <Route path="insights/content" element={<AgentInsightsContentPage />} />
                <Route path="insights/workflows" element={<AgentInsightsWorkflowsPage />} />
                <Route path="insights/time-patterns" element={<AgentInsightsTimePatternsPage />} />
                <Route path="insights/dropoffs" element={<AgentInsightsDropoffsPage />} />
                <Route path="insights/revival" element={<AgentInsightsRevivalPage />} />
                <Route path="insights/categories" element={<AgentInsightsCategoriesPage />} />
                <Route path="nexus" element={<AgentKnowledgeBankPage />} />
                <Route path="settings" element={<AgentSettingsPage />} />
                <Route path="account" element={<AgentAccountSettingsPage />} />
                <Route path="foundry" element={<AgentFoundryPage />} />
                <Route path="admin/clients" element={<AgentAdminClientsPage />} />
                <Route path="admin/clients/create" element={<AgentAdminCreateClientPage />} />
              </Route>

              {/* Magic Link routes (public - no auth required) */}
              <Route path="/magic" element={<MagicLinkLayout />}>
                <Route path="uploads/reference_letter" element={<ReferenceLetterUploadPage />} />
                <Route path="account/delete" element={<DeleteAccountPage />} />
                <Route path="book-meeting" element={<BookMeetingPage />} />
              </Route>

              {/* Demo routes (public - no auth required) */}
              <Route path="/demo">
                <Route path="home" element={<DemoHomePage />} />
                <Route path="hotel" element={<DemoHomePage_Hotel />} />
                <Route path="micampus" element={<DemoHomePage_MiCampus />} />
                <Route path="lbftravel" element={<DemoHomePage_LBFTravel />} />
                <Route path="europcar" element={<DemoHomePage_Europcar />} />
                <Route path="casa-andina" element={<DemoHomePage_CasaAndina />} />
                <Route path="corinthia" element={<DemoHomePage_Corinthia />} />
                <Route path="deyaar" element={<DemoHomePage_Deyaar />} />
                <Route path="sunlife" element={<DemoHomePage_SunLife />} />
                <Route path="yugo" element={<DemoHomePage_Yugo />} />
                <Route path="vamos" element={<DemoHomePage_Vamos />} />
                <Route path="mgm-macau" element={<DemoHomePage_MGMMacau />} />
                <Route path="phh" element={<DemoHomePage_PHH />} />
              </Route>

              {/* Redirect root to login */}
              <Route path="/" element={<Navigate to="/auth/login" replace />} />
              <Route path="*" element={<Navigate to="/auth/login" replace />} />
            </Routes>
            <Cookie />
          </BrowserRouter>
        </AuthProvider>
      </TrpcProvider>
    </ThemeProvider>
  );
}
